import { queryOptions, type QueryKey } from '@tanstack/react-query';

/**
 * Tiny wrapper around TanStack Query's `queryOptions` that locks a key to its
 * fetcher. Features use this in their `api/` folder to produce reusable option
 * bundles that hooks, prefetch calls, and `queryClient.invalidateQueries` all
 * share.
 *
 *   // features/catalog/api/productList.ts
 *   import { queryKeys } from '@sshop/api-client/keys';
 *   import { api } from '@/shared/lib/apiClient';
 *
 *   export const productListQuery = (filters: ProductListFilters) =>
 *     defineQuery({
 *       queryKey: queryKeys.product.list(filters),
 *       queryFn: () => api.products.list(filters),
 *       staleTime: 60_000,
 *     });
 *
 *   // hook
 *   export const useProductList = (f: ProductListFilters) =>
 *     useQuery(productListQuery(f));
 *
 *   // prefetch in a loader / route
 *   queryClient.prefetchQuery(productListQuery(f));
 *
 *   // invalidate
 *   queryClient.invalidateQueries({ queryKey: queryKeys.product.all });
 *
 * Rule: a feature NEVER passes a raw object literal to `useQuery`. It always
 * goes through one of these factory functions.
 */
export const defineQuery = queryOptions;

export type { QueryKey };
